import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';

import { routes, toastService } from '@fly/shared';
import { TanstackTable } from '@fly/shared-ui';
import { ContentLayout } from '@fly/shared-ui';
import { Separator } from '@radix-ui/themes';

import type { ArtifactVersion } from '../../store/registry.service';
import { ArtifactVersionsInsights } from './components/ArtifactVersionsInsights';
import { useArtifactVersions } from './useArtifactVersions';

export default function ArtifactVersionsPage({
  packageType,
  artifactName,
  owner,
  name,

  isRepositoriesLoading,
  repositoryUrl,
}: {
  packageType: string;
  artifactName: string;
  owner?: string;
  name?: string;

  isRepositoriesLoading?: boolean;
  repositoryUrl?: string;
}) {
  const { t } = useTranslation();

  const {
    artifact,
    versions,
    columns,
    searchKey,
    isDataLoading,
    shouldRedirect,
    handleStateChange,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    setClientFilteredCount,
  } = useArtifactVersions({
    artifactName,
    packageType,
    isRepositoriesLoading,
  });

  useEffect(() => {
    if (shouldRedirect) {
      toastService.error(
        t('app.common.not_found.by_name', {
          defaultValue: `${artifactName} not found`,
          name: artifactName,
        })
      );
    }
  }, [shouldRedirect, artifactName, t]);

  if (shouldRedirect) {
    return <Navigate to={routes.DASHBOARD.REGISTRY.ROOT} replace />;
  }

  return (
    <>
      <Separator size='4' />
      <ContentLayout
        sideContent={
          <ArtifactVersionsInsights
            artifactDetails={artifact}
            isLoading={isDataLoading}
            repositoryUrl={repositoryUrl}
          />
        }
      >
        <TanstackTable<ArtifactVersion>
          layout='fixed'
          stateType='search'
          dataProcessing='server'
          paginationType='virtual_scroll'
          nextCursor={hasNextPage ? 'hasNextPage' : null}
          onLoadMore={fetchNextPage}
          loadingMore={isFetchingNextPage}
          data-testid='artifacts-versions-table'
          columns={columns}
          data={versions}
          searchKey={searchKey}
          onFilteredCountChange={setClientFilteredCount}
          onStateChange={handleStateChange}
          loading={isDataLoading}
        />
      </ContentLayout>
    </>
  );
}
